import React, { useState, useEffect, useMemo } from 'react';
import { 
  TrendingUp, 
  CreditCard, 
  DollarSign, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  User,
  Building2,
  PieChart,
  BarChart3,
  Calendar,
  FileText,
  RefreshCw
} from 'lucide-react';
import { MetricCard } from './MetricCard';
import { 
  BuyerFinancialProfile, 
  sampleBuyerFinancialProfile, 
  calculateBuyerFinancialMetrics,
  formatCurrency,
  formatPercentage,
  getCreditScoreColor,
  getRiskLevelColor
} from '../types/buyerFinancialData';
import { CreditScoreChart } from './buyerCharts/CreditScoreChart';
import { AssetLiabilityChart } from './buyerCharts/AssetLiabilityChart';
import { DebtToIncomeChart } from './buyerCharts/DebtToIncomeChart';
import { LiquidityAnalysisChart } from './buyerCharts/LiquidityAnalysisChart';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { DatabaseService } from '../lib/database';

interface BuyerFinancialAnalysisProps {
  buyerId?: string;
}

export function BuyerFinancialAnalysis({ buyerId }: BuyerFinancialAnalysisProps) {
  const [selectedTimeframe, setSelectedTimeframe] = useState<'current' | 'historical'>('current');
  const [isLoading, setIsLoading] = useState(false);
  const { error, handleError, clearError, retry } = useErrorHandler();
  
  // Load buyer profile data
  const [buyerProfile, setBuyerProfile] = useState<BuyerFinancialProfile | null>(null);
  
  useEffect(() => {
    if (buyerId) {
      loadBuyerProfile();
    } else {
      // Use sample data if no buyerId provided
      setBuyerProfile(calculateBuyerFinancialMetrics(sampleBuyerFinancialProfile));
    }
  }, [buyerId]);

  const loadBuyerProfile = async () => {
    setIsLoading(true);
    clearError();
    try {
      const response = await DatabaseService.getBuyerFinancialProfile(buyerId!);
      if (response.error) {
        console.error('Load buyer profile error:', response.error);
        handleError(response.error, 'load buyer profile');
        return;
      } else if (response.data) {
        // Transform database data to match the expected format
        const transformedProfile = {
          id: response.data.id,
          buyerId: response.data.creator,
          profileDate: response.data.profile_date,
          totalAssets: response.data.total_assets || 0,
          totalLiabilities: response.data.total_liabilities || 0,
          netWorth: response.data.net_worth || 0,
          monthlyIncome: response.data.monthly_income || 0,
          monthlyExpenses: response.data.monthly_expenses || 0,
          monthlyNetIncome: response.data.monthly_net_income || 0,
          debtToIncomeRatio: response.data.debt_to_income_ratio || 0,
          creditUtilizationRatio: response.data.credit_utilization_ratio || 0,
          liquidityRatio: response.data.liquidity_ratio || 0,
          riskScore: response.data.risk_score || 0,
          sbaEligibilityScore: response.data.sba_eligibility_score || 0,
          lastUpdated: response.data.last_updated,
          creditScores: response.data.creditScores?.map((score: any) => ({
            bureau: score.bureau,
            score: score.score,
            date: score.score_date,
            factors: {
              paymentHistory: score.payment_history,
              creditUtilization: score.credit_utilization,
              lengthOfCreditHistory: score.length_of_credit_history,
              newCredit: score.new_credit,
              creditMix: score.credit_mix
            }
          })) || [],
          bankAccounts: response.data.bankAccounts?.map((account: any) => ({
            id: account.id,
            accountType: account.account_type,
            bankName: account.bank_name,
            accountNumber: account.account_number,
            currentBalance: account.current_balance,
            averageBalance: account.average_balance,
            monthlyInflow: account.monthly_inflow,
            monthlyOutflow: account.monthly_outflow,
            statementDate: account.statement_date
          })) || [],
          sbaPersonalFinancialStatement: {
            id: 'pfs-1',
            statementDate: response.data.sbaPersonalFinancialStatement?.statement_date || new Date().toISOString().split('T')[0],
            personalInfo: response.data.sbaPersonalFinancialStatement?.personal_info || {},
            assets: response.data.assets?.map((asset: any) => ({
              type: asset.asset_type,
              description: asset.description,
              value: asset.current_value,
              isLiquid: asset.is_liquid,
              monthlyIncome: asset.monthly_income
            })) || [],
            liabilities: response.data.liabilities?.map((liability: any) => ({
              type: liability.liability_type,
              description: liability.description,
              balance: liability.current_balance,
              monthlyPayment: liability.monthly_payment,
              interestRate: liability.interest_rate
            })) || [],
            incomeSources: response.data.incomeSources?.map((income: any) => ({
              type: income.income_type,
              description: income.description,
              monthlyAmount: income.monthly_amount,
              annualAmount: income.annual_amount,
              isGuaranteed: income.is_guaranteed,
              startDate: income.start_date,
              employer: income.employer
            })) || []
          },
          taxReturns: response.data.taxReturns?.map((taxReturn: any) => ({
            year: taxReturn.tax_year,
            filingStatus: taxReturn.filing_status,
            adjustedGrossIncome: taxReturn.adjusted_gross_income,
            taxableIncome: taxReturn.taxable_income,
            totalTax: taxReturn.total_tax,
            w2Income: taxReturn.w2_income,
            businessIncome: taxReturn.business_income,
            investmentIncome: taxReturn.investment_income,
            otherIncome: taxReturn.other_income,
            isFiled: taxReturn.is_filed,
            filingDate: taxReturn.filing_date
          })) || [],
          metadata: response.data.metadata || {}
        };
        setBuyerProfile(calculateBuyerFinancialMetrics(transformedProfile));
      } else {
        // Use sample data with the buyerId
        const profileWithId = {
          ...sampleBuyerFinancialProfile,
          buyerId: buyerId || sampleBuyerFinancialProfile.buyerId
        };
        setBuyerProfile(calculateBuyerFinancialMetrics(profileWithId));
      }
    } catch (err) {
      console.error('Load buyer profile error:', err);
      handleError(err, 'load buyer profile');
    } finally {
      setIsLoading(false);
    }
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading buyer profile...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center max-w-md mx-auto">
          <div className="w-16 h-16 mx-auto mb-4 text-red-500">
            <AlertTriangle className="w-full h-full" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Error Loading Financial Profile
          </h2>
          <p className="text-gray-600 mb-4">{error.message}</p>
          {process.env.NODE_ENV === 'development' && error.details && (
            <details className="mb-4 text-left">
              <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
                Error Details (Development Mode)
              </summary>
              <div className="mt-2 p-3 bg-gray-100 rounded-lg text-xs font-mono text-gray-800 overflow-auto max-h-32">
                <pre className="whitespace-pre-wrap">{JSON.stringify(error.details, null, 2)}</pre>
              </div>
            </details>
          )}
          <button 
            onClick={() => retry(loadBuyerProfile)} 
            className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show no profile state
  if (!buyerProfile) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Profile Found</h3>
          <p className="text-gray-600">
            {buyerId 
              ? 'No buyer profile found for the selected buyer.'
              : 'No buyer profile data available.'
            }
          </p>
        </div>
      </div>
    );
  }

  // Calculate key metrics
  const averageCreditScore = buyerProfile.creditScores.reduce((sum, score) => sum + score.score, 0) / buyerProfile.creditScores.length;
  const creditScoreRange = averageCreditScore >= 750 ? 'excellent' : averageCreditScore >= 700 ? 'good' : averageCreditScore >= 650 ? 'fair' : 'poor';
  
  const liquidAssets = buyerProfile.bankAccounts.reduce((sum, account) => sum + account.currentBalance, 0);
  const totalDebtPayments = buyerProfile.sbaPersonalFinancialStatement.liabilities.reduce((sum, liability) => sum + liability.monthlyPayment, 0);
  
  // Calculate SBA-specific metrics
  const sbaLiquidityRequirement = buyerProfile.monthlyExpenses * 3; // 3 months of expenses
  const meetsLiquidityRequirement = liquidAssets >= sbaLiquidityRequirement;
  const sbaDebtToIncomeThreshold = 40; // SBA typically requires DTI < 40%
  const meetsDTIRequirement = buyerProfile.debtToIncomeRatio < sbaDebtToIncomeThreshold;

  // Risk assessment
  const riskFactors = [];
  if (buyerProfile.debtToIncomeRatio > 40) riskFactors.push('High debt-to-income ratio');
  if (buyerProfile.liquidityRatio < 3) riskFactors.push('Low liquidity ratio');
  if (averageCreditScore < 650) riskFactors.push('Below average credit score');
  if (buyerProfile.monthlyNetIncome < 0) riskFactors.push('Negative monthly cash flow');

  const strengths = [];
  if (averageCreditScore >= 700) strengths.push('Strong credit score');
  if (buyerProfile.liquidityRatio >= 6) strengths.push('Excellent liquidity position');
  if (buyerProfile.debtToIncomeRatio < 30) strengths.push('Low debt-to-income ratio');
  if (buyerProfile.monthlyNetIncome > 2000) strengths.push('Strong positive cash flow');

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      {/* Header Section */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-2 flex items-center gap-2">
          <User className="w-5 h-5 text-blue-600" />
          Buyer Financial Profile Analysis
          {buyerId && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              (ID: {buyerId})
            </span>
          )}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Comprehensive analysis of buyer's financial position for SBA loan qualification assessment.
          Based on SBA Personal Financial Statement, Tax Returns, Bank Statements, and Credit Reports.
        </p>
        {buyerProfile.sbaPersonalFinancialStatement.personalInfo.name && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-medium text-blue-900">
                  {buyerProfile.sbaPersonalFinancialStatement.personalInfo.name}
                </h3>
                <p className="text-sm text-blue-700">
                  {buyerProfile.sbaPersonalFinancialStatement.personalInfo.email}
                </p>
                <p className="text-xs text-blue-600">
                  Profile Date: {new Date(buyerProfile.profileDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Key Financial Metrics */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-green-600" />
          Key Financial Metrics
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          <MetricCard
            title="Net Worth"
            value={buyerProfile.netWorth}
            className="border-l-4 border-l-blue-500"
            sublabel={`Assets: ${formatCurrency(buyerProfile.totalAssets)} | Liabilities: ${formatCurrency(buyerProfile.totalLiabilities)}`}
          />
          <MetricCard
            title="Monthly Income"
            value={buyerProfile.monthlyIncome}
            className="border-l-4 border-l-green-500"
            sublabel={`Annual: ${formatCurrency(buyerProfile.monthlyIncome * 12)}`}
          />
          <MetricCard
            title="Debt-to-Income Ratio"
            value={buyerProfile.debtToIncomeRatio}
            format="percentage"
            className={`border-l-4 ${meetsDTIRequirement ? 'border-l-green-500' : 'border-l-red-500'}`}
            sublabel={`SBA Requirement: < ${sbaDebtToIncomeThreshold}%`}
          />
          <MetricCard
            title="Liquidity Ratio"
            value={buyerProfile.liquidityRatio}
            format="decimal"
            className={`border-l-4 ${meetsLiquidityRequirement ? 'border-l-green-500' : 'border-l-yellow-500'}`}
            sublabel={`Months of expenses covered`}
          />
        </div>
      </div>

      {/* Credit Analysis Chart */}
      <div className="mb-8">
        <CreditScoreChart creditScores={buyerProfile.creditScores} />
      </div>

      {/* Asset & Liability Analysis Chart */}
      <div className="mb-8">
        <AssetLiabilityChart 
          assets={buyerProfile.sbaPersonalFinancialStatement.assets}
          liabilities={buyerProfile.sbaPersonalFinancialStatement.liabilities}
        />
      </div>

      {/* Debt-to-Income Analysis Chart */}
      <div className="mb-8">
        <DebtToIncomeChart 
          liabilities={buyerProfile.sbaPersonalFinancialStatement.liabilities}
          incomeSources={buyerProfile.sbaPersonalFinancialStatement.incomeSources}
        />
      </div>

      {/* Liquidity Analysis Chart */}
      <div className="mb-8">
        <LiquidityAnalysisChart 
          bankAccounts={buyerProfile.bankAccounts}
          monthlyExpenses={buyerProfile.monthlyExpenses}
        />
      </div>

      {/* SBA Eligibility Assessment */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Shield className="w-5 h-5 text-emerald-600" />
          SBA Loan Eligibility Assessment
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Eligibility Score */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Overall Eligibility Score</h4>
            <div className="text-center">
              <div className={`text-4xl font-bold mb-2 ${getRiskLevelColor(buyerProfile.sbaEligibilityScore).split(' ')[0]}`}>
                {Math.round(buyerProfile.sbaEligibilityScore)}
              </div>
              <div className="text-sm text-gray-600 mb-4">out of 100</div>
              <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getRiskLevelColor(buyerProfile.sbaEligibilityScore)}`}>
                {buyerProfile.sbaEligibilityScore >= 80 ? 'Excellent' : 
                 buyerProfile.sbaEligibilityScore >= 60 ? 'Good' : 
                 buyerProfile.sbaEligibilityScore >= 40 ? 'Fair' : 'Poor'}
              </div>
            </div>
          </div>

          {/* SBA Requirements */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">SBA Requirements</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">Credit Score ≥ 650</div>
                <div className="flex items-center gap-2">
                  {averageCreditScore >= 650 ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm font-medium">{Math.round(averageCreditScore)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">DTI Ratio &lt; 40%</div>
                <div className="flex items-center gap-2">
                  {meetsDTIRequirement ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm font-medium">{formatPercentage(buyerProfile.debtToIncomeRatio)}</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">3+ Months Liquidity</div>
                <div className="flex items-center gap-2">
                  {meetsLiquidityRequirement ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm font-medium">{buyerProfile.liquidityRatio.toFixed(1)} months</span>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-600">Positive Cash Flow</div>
                <div className="flex items-center gap-2">
                  {buyerProfile.monthlyNetIncome > 0 ? (
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                  <span className="text-sm font-medium">{formatCurrency(buyerProfile.monthlyNetIncome)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Risk Assessment */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Risk Assessment</h4>
            <div className="space-y-3">
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${getRiskLevelColor(buyerProfile.riskScore).split(' ')[0]}`}>
                  {Math.round(buyerProfile.riskScore)}
                </div>
                <div className="text-sm text-gray-600 mb-4">Risk Score</div>
              </div>
              {riskFactors.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-2">Risk Factors:</div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {riskFactors.map((factor, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <AlertTriangle className="w-3 h-3 text-red-500" />
                        {factor}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {strengths.length > 0 && (
                <div>
                  <div className="text-sm font-medium text-gray-900 mb-2">Strengths:</div>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {strengths.map((strength, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <CheckCircle className="w-3 h-3 text-green-500" />
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Income Analysis */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-green-600" />
          Income Analysis
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Income Sources */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Income Sources</h4>
            <div className="space-y-3">
              {buyerProfile.sbaPersonalFinancialStatement.incomeSources.map((income) => (
                <div key={income.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">{income.description}</div>
                    <div className="text-sm text-gray-600">
                      {income.employer && `${income.employer} • `}
                      {income.isGuaranteed ? 'Guaranteed' : 'Variable'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(income.monthlyAmount)}
                    </div>
                    <div className="text-sm text-gray-600">
                      {formatCurrency(income.annualAmount)}/year
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tax Return Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h4 className="font-semibold text-gray-900 mb-4">Recent Tax Returns</h4>
            <div className="space-y-3">
              {buyerProfile.taxReturns.slice(0, 2).map((taxReturn) => (
                <div key={taxReturn.year} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900">Tax Year {taxReturn.year}</div>
                    <div className="text-sm text-gray-600">
                      {taxReturn.filingStatus.replace('_', ' ')} • 
                      {taxReturn.isFiled ? ' Filed' : ' Not Filed'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold text-gray-900">
                      {formatCurrency(taxReturn.adjustedGrossIncome)}
                    </div>
                    <div className="text-sm text-gray-600">
                      AGI
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-blue-600" />
          Recommendations & Next Steps
        </h3>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">Immediate Actions</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                {!meetsDTIRequirement && (
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                    Consider debt consolidation to reduce monthly payments
                  </li>
                )}
                {!meetsLiquidityRequirement && (
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5" />
                    Build emergency fund to meet 3-month expense requirement
                  </li>
                )}
                {averageCreditScore < 700 && (
                  <li className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5" />
                    Work on improving credit score before applying
                  </li>
                )}
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Gather all required documentation (bank statements, tax returns)
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                  Prepare detailed business plan and financial projections
                </li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">SBA Loan Preparation</h4>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <Building2 className="w-4 h-4 text-blue-500 mt-0.5" />
                  Complete SBA Personal Financial Statement (Form 413)
                </li>
                <li className="flex items-start gap-2">
                  <FileText className="w-4 h-4 text-blue-500 mt-0.5" />
                  Obtain 3 years of personal tax returns
                </li>
                <li className="flex items-start gap-2">
                  <CreditCard className="w-4 h-4 text-blue-500 mt-0.5" />
                  Get credit reports from all three bureaus
                </li>
                <li className="flex items-start gap-2">
                  <DollarSign className="w-4 h-4 text-blue-500 mt-0.5" />
                  Provide 3 months of bank statements
                </li>
                <li className="flex items-start gap-2">
                  <Calendar className="w-4 h-4 text-blue-500 mt-0.5" />
                  Schedule meeting with SBA-preferred lender
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
